import { Suspense, lazy, useState, useEffect } from 'react';
import LoadingScreen from 'src/componentes/LoadingScreen';
import { Row, Col } from 'src/componentes/grid';
import { connect } from 'react-redux';
import Button from 'src/componentes/button';
import http from 'src/services/httpService';
import { getConfiguration } from 'src/services/commonRequests';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';
import Avatar from 'src/componentes/item-multi-lookup';
import Input from 'src/componentes/input';
import Filtro from 'src/componentes/filtro';
import ContentManager from 'src/componentes/content-manager';
import { FILTROS } from 'src/utils/constants';
import { getFilter } from 'src/componentes/NewList';
import FiltroChip from 'src/componentes/filtro-chip';
import useAuth from 'src/hooks/useAuth';
import parseScoreRisco from 'src/utils/parseScoreRisco';

const GraficoGauge = lazy(() =>
  import('src/paginas/item/valor/componentes/grafico-gauge')
);
import GraficoScorePeriodo from 'src/paginas/gestao-risco/risco/Avaliacao/AvaliacaoEditViewNew/componentes/grafico-score-periodo';
import SearchSchema from './searchSchema';
import Search from './search';
import ToleranciaRiscoBarra from '../../Avaliacao/AvaliacaoEditViewNew/componentes/ToleranciaRiscoBarra';
import LucideIcon from 'src/componentes/lucide-icon';

const Graficos = ({
  model: { value },
  exibirGraficoScorePeriodo = true,
  exibirGraficoScorePeriodoGauge = true,
  resources,
  updater,
  ...props
}) => {
  const getFilterDefaultChart = () => {
    return {
      valor: {
        tipoFiltroData: null,
        dataInicio: null,
        dataTermino: null,
      },
    };
  };

  const { decimalSeparator, thousandsSeparator } = useAuth();
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [avaliacaoGauge, setAvaliacaoGauge] = useState(null);
  const [searchModelState, setSearchModel] = useState(getFilterDefaultChart());
  const [grausDaMatrizRisco, setGrausDaMatriz] = useState([]);
  const [maiorScoreDaMatriz, setMaiorScoreDaMatriz] = useState([]);
  const [configuracao, setConfiguracao] = useState(null);
  const [toleranciaRisco, setToleranciaRisco] = useState({});

  const isModelNotNull = (modelValue) =>
    modelValue && Object.keys(modelValue).length > 0;

  useEffect(() => {
    getConfiguration('ItemConfiguracaoRisco')(setConfiguracao)();
  }, []);

  const refresh = async (searchModel) => {
    if (configuracao) {
      if (!searchModel) {
        searchModel = await getFilter(FILTROS.RISCO_GRAFICOSCORE, true);
        if (!searchModel || !searchModel.valor?.tipoFiltroData)
          searchModel = getFilterDefaultChart();

        setSearchModel(searchModel);
      }

      if (isModelNotNull(value) && value.id > 0) {
        try {
          const {
            data: {
              avaliacoes,
              avaliacaoGauge,
              grausDaMatriz,
              maiorScoreDaMatriz,
              toleranciaRisco,
            },
          } = await http.post('/RiscoAvaliacao/ObterGraficoDeScore', {
            riscoId: value.id,
            model:
              searchModel && searchModel.valor
                ? searchModel.valor
                : searchModel,
          });

          setAvaliacoes(avaliacoes);
          setAvaliacaoGauge(avaliacaoGauge);
          setGrausDaMatriz(grausDaMatriz);
          setMaiorScoreDaMatriz(maiorScoreDaMatriz);
          setToleranciaRisco(toleranciaRisco);
        } catch (err) {
          errorHandler(err);
        }
      }
    }
  };

  useEffect(() => {
    if (value && value.id !== -1) refresh();
  }, [configuracao, updater, value]);

  const handleSearch = (searchModel) => {
    setSearchModel(searchModel);

    if (searchModel && searchModel.valor) refresh(searchModel.valor);
  };

  const formatGaugeDisplayValue = (inputValue, exibirDecimais) => {
    if (inputValue === null || inputValue === undefined || inputValue === '') {
      return '';
    }

    const numericValue = Number(inputValue);
    if (Number.isNaN(numericValue)) return inputValue;

    const decimal = decimalSeparator || ',';
    const thousands = thousandsSeparator || '.';
    const casasDecimais = exibirDecimais ? 2 : 0;
    const [integerPart, decimalPart] = numericValue
      .toFixed(casasDecimais)
      .split('.');
    const integerWithSeparator = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      thousands
    );

    return decimalPart
      ? `${integerWithSeparator}${decimal}${decimalPart}`
      : integerWithSeparator;
  };

  const generateGaugeGraph = () => {
    const gaugeValue =
      avaliacaoGauge &&
      parseScoreRisco(
        avaliacaoGauge.score,
        thousandsSeparator,
        decimalSeparator
      );

    const valoresDaEscala =
      grausDaMatrizRisco && grausDaMatrizRisco.length > 0
        ? grausDaMatrizRisco.flatMap(({ de, ate }) => [de, ate])
        : [];
    const exibirDecimais = [maiorScoreDaMatriz, gaugeValue, ...valoresDaEscala]
      .map((value) => Number(value))
      .some((value) => !Number.isNaN(value) && !Number.isInteger(value));

    return {
      chart: {
        lowerLimit: '0',
        lowerLimitDisplay: formatGaugeDisplayValue(0, exibirDecimais),
        upperLimit: maiorScoreDaMatriz,
        upperLimitDisplay: formatGaugeDisplayValue(
          maiorScoreDaMatriz,
          exibirDecimais
        ),
        decimalSeparator: decimalSeparator || ',',
        thousandSeparator: thousandsSeparator || '.',
        numberDecimalSeparator: decimalSeparator || ',',
        numberThousandSeparator: thousandsSeparator || '.',
        formatNumber: '1',
        formatNumberScale: '0',
        inDecimalSeparator: '.',
        inThousandSeparator: ',',
        tickValueDecimals: exibirDecimais ? '2' : '0',
        forceTickValueDecimals: exibirDecimais ? '1' : '0',
        showValue: '1',
        theme: 'fusion',
        showToolTip: '0',
        decimals: '2',
        forceDecimals: '0',
      },
      color:
        grausDaMatrizRisco &&
        grausDaMatrizRisco.length > 0 &&
        grausDaMatrizRisco.map(({ de, ate, grauRisco: { cor } }, idx) => {
          const limiteSuperiorFaixa =
            idx === grausDaMatrizRisco.length - 1 ? maiorScoreDaMatriz : ate;

          return {
            minValue: de,
            maxValue: limiteSuperiorFaixa,
            label: `${formatGaugeDisplayValue(
              de,
              exibirDecimais
            )} - ${formatGaugeDisplayValue(
              limiteSuperiorFaixa,
              exibirDecimais
            )}`,
            code: cor,
          };
        }),
      value: gaugeValue ?? '',
      displayValue: formatGaugeDisplayValue(gaugeValue, exibirDecimais),
    };
  };

  const getAvatar = (users) =>
    users &&
    users.length > 0 && (
      <div>
        <div className={'ml-1 mb-1'}>
          {capitalizeFirstLetter(resources.responsaveis)}
        </div>
        <div>
          {users.map((user, index) => (
            <Avatar
              key={`avatarResponsavelModalEdicaoRisco-${
                user?.id ?? user?.value ?? user?.nome ?? index
              }`}
              foto={user.fotoArquivo}
              onlyAvatar={false}
              disabled
              avatar
              label={user.nome}
              value={user.value}
            ></Avatar>
          ))}
        </div>
      </div>
    );

  const getOrganizacao = (area) => (
    <Input
      style={{ margin: 0 }}
      disabled
      model={{
        label: capitalizeFirstLetter(resources.area),
        value: area.nome,
      }}
    />
  );

  const hasValue = (value) => value && value !== '';

  const handleFiltroModal = () => {
    ContentManager.addContent(
      <Filtro
        {...props}
        filtroId={FILTROS.RISCO_GRAFICOSCORE}
        usarFiltroSalvo={true}
        searchComponent={<Search />}
        searchSchema={SearchSchema}
        onSearch={handleSearch}
        showModal={true}
        selectedFilter={
          searchModelState ? searchModelState : getFilterDefaultChart()
        }
        ignorarCamposCustomizados={true}
      />
    );
  };

  const handleFiltroChip = () => {
    let searchModel =
      searchModelState && searchModelState.valor != null
        ? searchModelState.valor
        : searchModelState;
    return (
      <FiltroChip
        filtroId={FILTROS.RISCO_GRAFICOSCORE}
        onSearch={handleSearch}
        searchSchema={SearchSchema}
        selectedFilter={searchModel}
        onRemoveSaveFilter={true}
        dateFormat={'MM/YYYY'}
      />
    );
  };

  const exibirGraficos =
    configuracao &&
    (configuracao.exibirGraficoVelocimetroGrauRisco ||
      configuracao.exibirGraficoScoreXTempoRisco ||
      configuracao.exibirFaixaToleranciaRisco);

  const exibirGraficoVelocimetro =
    configuracao &&
    configuracao.habilitarCalculoDeScores &&
    configuracao.exibirGraficoVelocimetroGrauRisco &&
    exibirGraficoScorePeriodoGauge;

  const exibirGraficoTolerancia =
    configuracao &&
    configuracao.exibirFaixaToleranciaRisco &&
    hasValue(toleranciaRisco);

  const exibirGraficoScoreXTempo =
    configuracao &&
    configuracao.exibirGraficoScoreXTempoRisco &&
    exibirGraficoScorePeriodo;

  return (
    <Suspense fallback={<LoadingScreen />}>
      {configuracao && value && value.id > 0 ? (
        <div className={'card mt-3'}>
          <Row>
            <Col md={7}>
              <div
                className="col-md-12 d-flex"
                style={{ margin: '20px 0px 1px 20px' }}
              >
                <div className="col-md-4">
                  {value.area && getOrganizacao(value.area)}
                </div>
                <div
                  className="col-md-8"
                  style={{ margin: '0px 0px 0px 10px' }}
                >
                  {getAvatar(value.responsaveis)}
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className={'mt-3 mr-1 pull-right'}>{handleFiltroChip()}</div>
            </Col>
            <Col md={1}>
              <div className={'mt-3 mr-3 pull-right'}>
                <Button
                  size="icon"
                  className="tw:ms-2"
                  onClick={handleFiltroModal}
                >
                  <LucideIcon icon="Funnel" />
                </Button>
              </div>
            </Col>
          </Row>
          {exibirGraficos && (
            <Row>
              {exibirGraficoVelocimetro && (
                <Col md={4}>
                  <GraficoGauge
                    backgroundTransparent={true}
                    grafico={generateGaugeGraph()}
                    height={250}
                  />
                </Col>
              )}
              {exibirGraficoTolerancia && (
                <>
                  <div className="pt-5" style={{ flex: '1' }}>
                    <ToleranciaRiscoBarra model={toleranciaRisco} />
                  </div>
                  <Col
                    md={configuracao.habilitarCalculoDeScores ? 1 : 6}
                    className={'mb-5 mt-5'}
                    style={{ maxWidth: '10px' }}
                  >
                    <div
                      style={{
                        background: '#e7ecee',
                        height: '100%',
                        width: 2,
                        marginLeft: 10,
                      }}
                    ></div>
                  </Col>
                </>
              )}
              {exibirGraficoScoreXTempo && (
                <Col
                  md={
                    exibirGraficoVelocimetro && exibirGraficoTolerancia
                      ? 6
                      : exibirGraficoVelocimetro && !exibirGraficoTolerancia
                      ? 8
                      : !exibirGraficoVelocimetro && exibirGraficoTolerancia
                      ? 6
                      : 12
                  }
                >
                  <GraficoScorePeriodo
                    riscoId={value.id}
                    handleSearch={handleSearch}
                    avaliacoes={avaliacoes}
                    grausDaMatriz={grausDaMatrizRisco}
                    maiorScoreDaMatriz={maiorScoreDaMatriz}
                    exibirGraficoScorePeriodo={exibirGraficoScorePeriodo}
                    exibirFiltros={false}
                    searchModel={searchModelState}
                    thousandsSeparator={thousandsSeparator}
                    decimalSeparator={decimalSeparator}
                  />
                </Col>
              )}
            </Row>
          )}
        </div>
      ) : null}
    </Suspense>
  );
};
function mapStateToProps(state) {
  return {
    resources: state.user.termos,
  };
}

export default connect(mapStateToProps)(Graficos);
